import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Clock, Scale, ArrowRight, Star, CheckCircle2, Phone,
  MessageSquare, Calculator, FolderOpen, Sparkles, Stethoscope,
} from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import SeoHead, { firmJsonLd, faqJsonLd } from "@/components/SeoHead";
import SpeedPromiseBadge from "@/components/SpeedPromiseBadge";
import SocialProofTicker from "@/components/SocialProofTicker";
import AnimatedCounter from "@/components/AnimatedCounter";

const HERO_IMG = "https://images.pexels.com/photos/8569166/pexels-photo-8569166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRUST_IMG = "https://images.pexels.com/photos/8560686/pexels-photo-8560686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TESTI_IMG = "https://images.unsplash.com/photo-1758600435798-fad26c19c314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBkaXZlcnNlJTIwcGVyc29uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgwNjY0MTYyfDA&ixlib=rb-4.1.0&q=85";

const TRUST_LOGOS = ["NY Daily News", "NJ.com", "ABA Journal", "Newsday", "Bloomberg Law"];

export default function Landing() {
  const t = useT();
  const faqItems = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SeoHead
        title="Apex MVA — NY & NJ Motor Vehicle Accident Lawyers · Free Case Review"
        description="Free 2-minute case review for car accident victims in New York and New Jersey. No fee unless we win. AI-powered case-value estimator. 24/7 intake. Hablamos español."
        keywords="motor vehicle accident lawyer, car accident attorney NY, NJ MVA attorney, free case review, no fee unless we win"
        jsonLd={{
          ...firmJsonLd(),
          subjectOf: faqJsonLd(faqItems),
        }}
      />
      {/* Urgency banner */}
      <div className="w-full bg-[#11322A] text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-6 py-2 text-center text-xs font-semibold tracking-wide lg:px-12">
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-[#E85D04]" />
            {t("urgency.banner")}
          </span>
          <span className="hidden sm:inline-block h-3 w-px bg-white/20" />
          <span className="inline-flex items-center gap-1 rounded-full bg-[#E85D04]/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[#FDFBF7]">
            {t("se_habla")}
          </span>
        </div>
      </div>

      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/92 to-[#FDFBF7]/55" aria-hidden />
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:gap-16 lg:px-12 lg:py-32">
          <div className="lg:col-span-7">
            <span className="pill animate-rise" data-testid="hero-pill">
              <span className="h-2 w-2 rounded-full bg-[#E85D04]" /> {t("hero.pill")}
            </span>
            <h1 className="mt-6 font-display text-4xl font-black tracking-tighter text-[#11322A] sm:text-5xl lg:text-6xl animate-rise-delay-1">
              {t("hero.h1.line1")}
              <br />
              <span className="text-[#E85D04]">{t("hero.h1.line2")}</span>
              <br />
              {t("hero.h1.line3")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#5C6B68] sm:text-lg animate-rise-delay-2">
              {t("hero.subhead")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 animate-rise-delay-3">
              <Link to="/intake" className="btn-primary" data-testid="hero-cta-intake">
                {t("hero.cta.intake")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/estimator" className="btn-secondary" data-testid="hero-cta-estimator">
                {t("hero.cta.estimator")}
              </Link>
            </div>
            <SpeedPromiseBadge />
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#5C6B68]">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("hero.bullet.2min")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("hero.bullet.nofee")}</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" /> {t("hero.bullet.confidential")}</span>
            </div>
          </div>

          {/* Stats card */}
          <div className="lg:col-span-5">
            <div className="card-soft p-8" data-testid="hero-stats">
              <div className="label-cap">{t("hero.stats.title")}</div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {[
                  { n: "$340M+", l: t("hero.stat.recovered") },
                  { n: "12,400+", l: t("hero.stat.cases") },
                  { n: "4.9★", l: t("hero.stat.rating") },
                  { n: "24/7", l: t("hero.stat.hotline") },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-3xl font-black text-[#11322A]">
                      <AnimatedCounter value={s.n} />
                    </div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-[#5C6B68]">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-xl p-5" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-[#11322A] text-white">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#5C6B68]">{t("hero.help_now")}</div>
                    <a href="tel:+18005551234" className="font-display text-xl font-black text-[#11322A] hover:text-[#E85D04]" data-testid="hero-phone">
                      1-800-555-1234
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#E0E4E2] bg-white py-6">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-6 lg:px-12">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C6B68]">{t("trust.featured")}</span>
          {TRUST_LOGOS.map((l) => (
            <span key={l} className="font-display text-sm font-bold uppercase tracking-[0.1em] text-[#11322A]/70">{l}</span>
          ))}
        </div>
      </section>

      {/* TOOLS QUICK NAV */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { to: "/intake", icon: ArrowRight, t: t("tools.intake.title"), d: t("tools.intake.desc"), cta: t("tools.intake.cta") },
              { to: "/find-care", icon: Stethoscope, t: t("tools.find_care.title"), d: t("tools.find_care.desc"), cta: t("tools.find_care.cta") },
              { to: "/estimator", icon: Calculator, t: t("tools.estimator.title"), d: t("tools.estimator.desc"), cta: t("tools.estimator.cta") },
              { to: "/chat", icon: MessageSquare, t: t("tools.chat.title"), d: t("tools.chat.desc"), cta: t("tools.chat.cta") },
            ].map((tool) => (
              <Link
                key={tool.to}
                to={tool.to}
                className="card-soft group flex items-center justify-between p-6 transition hover:border-[#11322A] hover:shadow-md"
                data-testid={`tool-${tool.to}`}
              >
                <div>
                  <div className="font-display text-lg font-bold text-[#0B1317]">{tool.t}</div>
                  <div className="mt-1 text-sm text-[#5C6B68]">{tool.d}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#E85D04]">
                    {tool.cta} <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                  </div>
                </div>
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#FDFBF7] text-[#11322A] transition group-hover:bg-[#11322A] group-hover:text-white">
                  <tool.icon className="h-5 w-5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FIND CARE PROMO */}
      <section className="bg-white py-20 lg:py-24" data-testid="find-care-promo">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="lg:col-span-7">
            <span className="label-cap">{t("findcare.section_label")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
              {t("findcare.section_title")}
            </h2>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#5C6B68]">
              {t("findcare.section_body")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/find-care" className="btn-primary" data-testid="findcare-cta-promo">
                <Stethoscope className="h-4 w-4" /> {t("findcare.section_cta")}
              </Link>
              <a href="tel:+18005551234" className="btn-secondary">
                <Phone className="h-4 w-4" /> {t("nav.hotline")}
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="pill"><ShieldCheck className="h-3 w-3" /> {t("findcare.tag.private")}</span>
              <span className="pill">{t("findcare.tag.insurance")}</span>
              <span className="pill" style={{ color: "#1d4ed8", borderColor: "#b3d4ff", background: "#E6F3FF" }}>{t("se_habla")}</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="card-soft p-6">
              <div className="label-cap">{t("findcare.preview.label")}</div>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  t("findcare.preview.item1"),
                  t("findcare.preview.item2"),
                  t("findcare.preview.item3"),
                  t("findcare.preview.item4"),
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2D6A4F]" />
                    <span className="text-[#0B1317]">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-xl border border-[#bce5cc] bg-[#E6F6ED] p-3 text-xs text-[#166534]">
                <span className="font-bold">{t("findcare.preview.privacy_title")}:</span> {t("findcare.preview.privacy_body")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRACK YOUR CASE */}
      <section className="bg-[#FDFBF7] py-24 lg:py-32" data-testid="track-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="lg:col-span-6">
            <span className="label-cap">{t("track.label")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
              {t("track.title")}
            </h2>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[#5C6B68]">
              {t("track.body")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-primary" data-testid="track-signup">
                <FolderOpen className="h-4 w-4" /> {t("track.cta_signup")}
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="track-login">{t("track.cta_login")}</Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="card-soft p-8" data-testid="track-preview">
              <div className="flex items-center justify-between">
                <span className="label-cap">{t("track.case_id_label")} · NY-2026-0418</span>
                <span className="status-contacted inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider">{t("track.status.contacted")}</span>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-4">
                {[
                  t("track.status.submitted"),
                  t("track.status.contacted"),
                  t("track.status.accepted"),
                  t("track.status.resolved"),
                ].map((label, i) => {
                  const done = i <= 1;
                  return (
                    <div key={label} className="flex items-start gap-2">
                      <div className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full ${done ? "bg-[#11322A] text-white" : "border border-[#E0E4E2] bg-white text-[#5C6B68]"}`}>
                        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="text-xs font-bold">{i + 1}</span>}
                      </div>
                      <div className={`text-[11px] font-bold uppercase tracking-[0.1em] ${done ? "text-[#11322A]" : "text-[#5C6B68]"}`}>{label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 rounded-xl border border-[#E0E4E2] bg-[#FDFBF7] p-4 text-sm text-[#0B1317]">
                <span className="font-bold">{t("track.latest_update")}</span>
                <p className="mt-1 text-[#5C6B68]">{t("track.latest_body")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <span className="label-cap">{t("how.label")}</span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
            {t("how.title")}
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: t("how.step1.title"), d: t("how.step1.desc") },
              { n: "02", t: t("how.step2.title"), d: t("how.step2.desc") },
              { n: "03", t: t("how.step3.title"), d: t("how.step3.desc") },
            ].map((s) => (
              <div key={s.n} className="card-soft p-8" data-testid={`how-step-${s.n}`}>
                <div className="font-display text-5xl font-black text-[#E85D04]/90">{s.n}</div>
                <div className="mt-4 font-display text-xl font-bold text-[#0B1317]">{s.t}</div>
                <p className="mt-3 text-[15px] leading-relaxed text-[#5C6B68]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US / EMPATHY */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-[#E0E4E2]">
              <img src={TRUST_IMG} alt="" className="h-[460px] w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="label-cap">{t("why.label")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
              {t("why.title")}
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-[#5C6B68]">
              {t("why.body")}
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[
                { i: ShieldCheck, t: t("why.feature.vetted") },
                { i: Clock, t: t("why.feature.response") },
                { i: Scale, t: t("why.feature.contingency") },
              ].map((f) => (
                <div key={f.t} className="card-soft p-5" data-testid={`why-card-${f.t}`}>
                  <f.i className="h-5 w-5 text-[#E85D04]" />
                  <div className="mt-3 font-display text-base font-bold text-[#0B1317]">{f.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-[#11322A] py-24 text-white lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="lg:col-span-7">
            <div className="flex gap-1 text-[#E85D04]">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={`star-${i}`} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <blockquote className="mt-6 font-display text-2xl font-bold leading-snug sm:text-3xl">
              "{t("testimonial.quote")}"
            </blockquote>
            <div className="mt-6 text-sm text-white/70">{t("testimonial.attribution")}</div>
          </div>
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src={TESTI_IMG} alt="" className="h-[420px] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="lg:col-span-4">
            <span className="label-cap">{t("faq.label")}</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
              {t("faq.title")}
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqItems.map((f, i) => (
                <AccordionItem key={`faq-${i}`} value={`item-${i}`} className="card-soft px-6" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-bold text-[#0B1317] hover:text-[#E85D04]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[#5C6B68]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="card-soft grain p-10 sm:p-16">
            <div className="grid items-center gap-8 lg:grid-cols-2">
              <div>
                <h3 className="font-display text-3xl font-bold tracking-tight text-[#0B1317] sm:text-4xl">
                  {t("final.title")}
                </h3>
                <p className="mt-4 text-[17px] text-[#5C6B68]">
                  {t("final.body")}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <Link to="/intake" className="btn-primary" data-testid="final-cta-intake">{t("final.cta.intake")}</Link>
                <Link to="/chat" className="btn-secondary" data-testid="final-cta-chat">{t("final.cta.chat")}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SocialProofTicker />

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E0E4E2] bg-white/95 px-3 py-3 backdrop-blur md:hidden" data-testid="mobile-sticky-cta">
        <div className="mx-auto flex max-w-md items-center gap-2">
          <a href="tel:+18005551234" className="btn-secondary !py-2.5 flex-1 text-xs" data-testid="mobile-call">
            <Phone className="h-3.5 w-3.5" /> {t("mobile.call")}
          </a>
          <a href="sms:+18005551234" className="btn-secondary !py-2.5 flex-1 text-xs" data-testid="mobile-text">
            <MessageSquare className="h-3.5 w-3.5" /> {t("mobile.text")}
          </a>
          <Link to="/intake" className="btn-primary !py-2.5 flex-[2] text-xs" data-testid="mobile-intake">
            {t("free_review")}
          </Link>
        </div>
      </div>
    </div>
  );
}
